import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'

interface ThemeApi {
  preference: ThemePreference
  /** What is actually on screen right now. */
  resolved: 'light' | 'dark'
  setPreference: (preference: ThemePreference) => void
}

const KEY = 'clinic.theme'
const ThemeContext = createContext<ThemeApi | null>(null)

function systemPrefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

function read(): ThemePreference {
  // localStorage, not IndexedDB: this is a display preference, not patient data,
  // and it must be readable synchronously before the first paint.
  const stored = window.localStorage?.getItem(KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'light'
}

/** The default is light, not `system`: dark is a real option for a dim
 *  consulting room, but it is the doctor's choice rather than the OS's. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(read)
  const [systemDark, setSystemDark] = useState(systemPrefersDark)

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!query) return
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const resolved: 'light' | 'dark' =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference

  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', resolved)
    // Native controls — selects, date pickers, scrollbars — follow this, and
    // without it they render light on a dark page.
    root.style.colorScheme = resolved
    // And the browser chrome around the app. index.html sets this once before
    // first paint; switching theme in Settings has to move it too, or the
    // phone keeps a white status bar sitting on top of a dark screen. The
    // values are --color-canvas in each theme.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', resolved === 'dark' ? '#0b0f16' : '#ffffff')
  }, [resolved])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    window.localStorage?.setItem(KEY, next)
  }, [])

  const value = useMemo<ThemeApi>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeApi {
  const api = useContext(ThemeContext)
  if (!api) throw new Error('useTheme must be used inside ThemeProvider')
  return api
}
